import JsSIP from "jssip";
import { toast } from "react-toastify";
import { SelfieSegmentation } from "@mediapipe/selfie_segmentation";
import { stateAction } from "../Actions/AuthAction";

import {
  RESET_CALL_STATE,
  SET_IN_APP_CALL_STATUS,
  SET_IN_APP_CALL_TYPE,
  SET_IS_CONSULTING,
  START_ACW,
  TOGGLE_HOLD,
  TOGGLE_MUTE,
} from "../Types/CallTypes";

const remoteAudio = new window.Audio();
remoteAudio.autoplay = true;

const callOptions = {
  mediaConstraints: {
    audio: true,

    video: {
      width: { ideal: 320 },
      height: { ideal: 240 },
      frameRate: { ideal: 10, max: 15 },
    },
  },

  pcConfig: {
    rtcpMuxPolicy: "require",
    iceServers: [],
  },
};

let session;
let existingSession;
let phoneRTC;
let localMediaStream;
let localProcessedStream;
let stopBackgroundBlur;
let localVideoStream;

function stopLocalMedia() {
  stopBackgroundBlur?.();
  stopBackgroundBlur = undefined;
  localProcessedStream?.getTracks().forEach((track) => track.stop());
  localMediaStream?.getTracks().forEach((track) => track.stop());
  localProcessedStream = undefined;
  localMediaStream = undefined;
};

async function createBackgroundBlurStream (cameraStream) {
  const cameraTrack = cameraStream.getVideoTracks()[0];
  const settings = cameraTrack.getSettings();
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.autoplay = true;
  video.srcObject = cameraStream;
  await video.play();
  const canvas = document.createElement("canvas");
  canvas.width = settings.width || 320;
  canvas.height = settings.height || 240;
  const context = canvas.getContext("2d");

  const segmentation = new SelfieSegmentation({
    locateFile: (file) =>
      `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`,
  });

  const backgroundImage = new Image();
  backgroundImage.src = "/video-call-background.jpg";
  await backgroundImage.decode();

  segmentation.setOptions({
    modelSelection: 1,
  });

  /* segmentation.onResults((results) => {
    context.save();

    // Draw the raw image
    context.drawImage(results.image, 0, 0, canvas.width, canvas.height);

    // Make the foreground (all pixels in the segmentation mask) transparent
    context.globalCompositeOperation = "destination-out";

    context.drawImage(
      results.segmentationMask,
      0,
      0,
      canvas.width,
      canvas.height
    );

    // With the foreground removed, blur and redraw the background
    context.filter = "blur(10px)";
    context.globalCompositeOperation = "source-over";
    context.drawImage(canvas, 0, 0);

    // Draw the raw image again behind the blurred background
    // The foreground will be visible through the transparent cutout
    context.filter = "blur(0px)";
    context.globalCompositeOperation = "destination-over";
    context.drawImage(results.image, 0, 0, canvas.width, canvas.height);
  }); */

  segmentation.onResults((results) => {
    context.save();
    context.clearRect(0, 0, canvas.width, canvas.height);

    // Keep only the person from the camera frame.
    context.drawImage(results.image, 0, 0, canvas.width, canvas.height);
    context.globalCompositeOperation = "destination-in";

    context.drawImage(
      results.segmentationMask,
      0,
      0,
      canvas.width,
      canvas.height
    );

    // Fill the transparent background with your image.
    context.globalCompositeOperation = "destination-over";
    context.drawImage(backgroundImage, 0, 0, canvas.width, canvas.height);

    context.restore();
  });

  const processedVideoStream = canvas.captureStream(15);

  const processedStream = new MediaStream([
    ...processedVideoStream.getVideoTracks(),
    ...cameraStream.getAudioTracks(),
  ]);

  let stopped = false;
  let processing = false;

  const processFrame = async () => {
    if (stopped) return;

    if (!processing && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      processing = true;

      try {
        await segmentation.send({ image: video });
      } catch (error) {
        console.error("Background blur frame error:", error);
      } finally {
        processing = false;
      }
    }

    requestAnimationFrame(processFrame);
  };

  processFrame();

  stopBackgroundBlur = () => {
    stopped = true;
    segmentation.close();
    video.pause();
    video.srcObject = null;
  };

  return processedStream;
};

async function prepareLocalMedia () {
  if (localProcessedStream) {
    return localProcessedStream;
  }

  localMediaStream = await navigator.mediaDevices.getUserMedia({
    audio: true,

    video: {
      width: { ideal: 320 },
      height: { ideal: 240 },
      frameRate: { ideal: 15, max: 15 },
    },
  });

  try {
    localProcessedStream = await createBackgroundBlurStream(localMediaStream);
  } catch (error) {
    console.error("Background blur unavailable. Using raw camera.", error);
    localProcessedStream = localMediaStream;
  }

  localVideoStream?.(localProcessedStream);
  return localProcessedStream;
};

function replaceVideoSenderTrack(peerConnection, mediaStream) {
  const videoTrack = mediaStream?.getVideoTracks()[0];
  if (!videoTrack) return;

  const sender = peerConnection
    .getSenders()
    .find((item) => item.track?.kind === "video");

  sender?.replaceTrack(videoTrack).catch((error) => {
    console.error("Unable to replace video track:", error);
  });
};

export const registerSIP = (
  username,
  password,
  displayname,
  sip,
  wsSocket,
  currentState,
  onRemoteAudioStream,
  onRemoteVideoStream,
  onLocalVideoStream,
) => {
  localVideoStream = onLocalVideoStream;

  return async (dispatch, getState) => {
    const socket = new JsSIP.WebSocketInterface(wsSocket);

    const configuration = {
      sockets: [socket],
      uri: sip,
      password: password,
      username: username,
      register: true,
      display_name: displayname,
    };

    phoneRTC = new JsSIP.UA(configuration);

    phoneRTC.on("registered", function (ev) {
      let lastStatus;

      const statusBeforeDisconnect = localStorage.getItem(
        "statusBeforeDisconnect"
      );

      if (statusBeforeDisconnect) {
        try {
          lastStatus = JSON.parse(statusBeforeDisconnect);
        } catch (error) {
          console.error("Invalid JSON in statusBeforeDisconnect:", error);
          lastStatus = undefined;
        }
      }

      console.log("registered:", ev, "last status: ", lastStatus); // ready

      dispatch({
        type: SET_IN_APP_CALL_STATUS,
        payload: "registered",
      });

      if (localStorage.getItem("isNotFirstLoad")) {
        if (lastStatus?.container?.includes("AUX")) {
          dispatch(
            stateAction(
              "AUX",
              lastStatus?.container?.split(" - ")[1],
              lastStatus?.container,
              "Orange"
            )
          );
        } else if (lastStatus?.container?.includes("ACW")) {
          dispatch(
            stateAction(
              "ACW",
              undefined,
              lastStatus?.container,
              "Yellow",
              "startACW"
            )
          );
        } else {
          dispatch(
            stateAction(
              "READY",
              undefined,
              lastStatus ? "READY" : "NOT BOUND",
              "Green",
              "phoneRTC start"
            )
          );
        }
      }
    });

    phoneRTC.on("disconnected", function (ev) {
      let lastStatus = JSON.parse(localStorage.getItem("agentStatus"));
      console.log("disconnected:", ev, "last status: ", lastStatus); // not bound

      localStorage.setItem(
        "statusBeforeDisconnect",
        JSON.stringify(lastStatus)
      );

      /* if (localStorage.getItem("token")) {
        dispatch(
          stateAction(
            lastStatus?.container?.split(" - ")[0],
            lastStatus?.container?.split(" - ")[1],
            lastStatus?.prevStatus?.split(" - ")[0],
            "DarkRed",
            "disconnected"
          )
        );
      } */
    });

    phoneRTC.on("registrationFailed", function (ev) {
      toast.error("Registering on SIP server failed with error: " + ev.cause); // not bound
      dispatch(
        stateAction(
          "NOT BOUND",
          undefined,
          currentState,
          "DarkRed",
          "registrationFailed"
        )
      );
    });

    phoneRTC.on("newRTCSession", function (ev) {
      console.log("newRTCSession:", ev);
      const newSession = ev.session;

      if (session) {
        console.log("session already existed! Creating another session.")
        existingSession = session;
      }

      session = newSession;

      dispatch({
        type: SET_IN_APP_CALL_STATUS,
        payload: "started",
      });

      session.on("peerconnection", (e) => {
        console.log("peerconnectiom", e);
        const peerconnection = e.peerconnection;

        peerconnection.ontrack = async function (event) {
          const [remoteStream] = event.streams;
          console.log("Remote track added:", event.streams);

          if (remoteStream) {
            if (event.track.kind === "video" && onRemoteVideoStream) {
              console.log("video remote track detected");
              onRemoteVideoStream(remoteStream);
  
              dispatch({
                type: SET_IN_APP_CALL_TYPE,
                payload: "video",
              });

              const stream = await prepareLocalMedia();
              replaceVideoSenderTrack(peerconnection, stream);
            }
          }

          if (event.track.kind === "audio") {
            console.log("audio remote track detected");
            remoteAudio.srcObject = remoteStream;
            onRemoteAudioStream && onRemoteAudioStream(remoteStream);
          }
        };
      });

      session.on("ended", (data) => {
        // const { stateCall, isParking } = getState().call;
        const { isConsulting, onHold } = getState().call;

        if (!existingSession) {
          dispatch({
            type: SET_IN_APP_CALL_STATUS,
            payload: "ended",
          });

          dispatch({
            type: SET_IN_APP_CALL_TYPE,
            payload: "",
          });
        }

        console.log(
          "ended",
          data,
          "isConsulting: ",
          isConsulting,
          "onHold: ",
          onHold
        );

        // if (isParking || stateCall.IsParked) {
        // if (stateCall.IsParked && data.originator === "remote") {
        if (isConsulting && onHold && data.originator === "remote") {
          dispatch({
            type: SET_IS_CONSULTING,
            payload: false,
          });

          session.terminate();
        }
        // } else {
        // dispatch(clearSessionCall());
        // }

        stopLocalMedia();

        if (existingSession) {
          session = existingSession;
          existingSession = undefined;
          console.log("Existing session detected! Restoring existing session.")
        } else {
          session = undefined;
        }
      });

      if (session.direction === "incoming") {
        session.answer(callOptions);
      } else {
        session.connection.addEventListener("track", function (event) {
          const [remoteStream] = event.streams;
          console.log("Remote track added:", event.streams);

          if (remoteStream) {
            if (event.track.kind === "audio" && onRemoteAudioStream) {
              console.log("audio remote track detected");
              onRemoteAudioStream(remoteStream);
            }

            if (event.track.kind === "video" && onRemoteVideoStream) {
              console.log("video remote track detected");
              onRemoteVideoStream(remoteStream);
  
              dispatch({
                type: SET_IN_APP_CALL_TYPE,
                payload: "video",
              });
            }
          }

          if (event.track.kind === "audio") {
            remoteAudio.srcObject = remoteStream;
          }
        });
      }
    });

    phoneRTC.start();
  };
};

export const clearSessionCall = (param) => {
  return (dispatch, getState) => {
    const { agentPermission } = getState().auth;
    const { goToACW, isTelemarketing } = getState().call;

    const addInteractionPermission = agentPermission.find(
      (permission) => permission.PermissionCode === "Contact.AddInteraction"
    );

    localStorage.setItem("isInCall", false);

    if (session) {
      phoneRTC.terminateSessions();
      session = undefined;
    }

    stopLocalMedia();

    if (
      addInteractionPermission &&
      addInteractionPermission.PermissionAccess === "A" &&
      goToACW
    ) {
      dispatch({ type: START_ACW });
      dispatch(stateAction("ACW", undefined, "BUSY", "Yellow", "startACW"));
    } else {
      if (!isTelemarketing) {
        dispatch({ type: RESET_CALL_STATE });
      }

      // Don't run when logout
      if (param !== "logout") {
        console.log("stateAction-EndCall");
        dispatch(stateAction("READY", undefined, "BUSY", "Green", "AfterCall"));
      }
    }

    //for LogOut wont trigger dispatch else
    if (param === "logout") {
      return;
    }
  };
};

export const endConsult = () => {
  return (dispatch) => session.terminate();
};

export const endSession = () => {
  return () => {
    if (session) {
      phoneRTC.terminateSessions();
      session = undefined;
    }

    stopLocalMedia();
  }
};

export const muteCall = () => {
  if (session) {
    if (session.isMuted().audio) {
      session.unmute({ audio: true });

      return (dispatch) => {
        dispatch({
          type: TOGGLE_MUTE,
          payload: { isMuted: false },
        });
      };
    } else {
      session.mute({ audio: true });

      return (dispatch) => {
        dispatch({
          type: TOGGLE_MUTE,
          payload: { isMuted: true },
        });
      };
    }
  }
};

export const holdCall = () => {
  console.log("hold session: ", session)

  if (session) {
    console.log("session exist");
    if (session.isOnHold().local) {
      console.log("session unhold");
      session.unhold();
      return (dispatch) => dispatch({ type: TOGGLE_HOLD });
    } else {
      console.log("session hold");
      session.hold();
      return (dispatch) => dispatch({ type: TOGGLE_HOLD });
    }
  } else {
    return (dispatch) => dispatch({ type: TOGGLE_HOLD });
  }
};

export const unregisterSIP = () => {
  if (phoneRTC) {
    return () => {
      stopLocalMedia();
      console.log("unregister", phoneRTC);
      phoneRTC.stop();
      phoneRTC.unregister();
    };
  }
};

export const callDirect = () => {
  if (phoneRTC) {
    return () => {
      console.log("start direct call");
      phoneRTC.call("6000", callOptions);
    };
  }
};

export const sendDTMF = (key) => {
  if (session) {
    return () => session.sendDTMF(Number(key));
  }
};
